import React, { useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import { MdModeEdit } from "react-icons/md";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Formik } from "formik";
import * as Yup from "yup";
import AdminPopUpDismissableModal from "components/adminPopupDismissableModal";
import { getErrorMessage } from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import { formatDateWithTime } from 'services/MomentUtils';
import { twoDecimalPlaceRounder } from 'services/UtilityService';
import "assets/css/user.css";

const EditBatch = ({ batch, showButton: initialShowButton, handler }) => {
  const [showButton, setShowButton] = useState(initialShowButton);
  const modalRef = useRef(null);

  const editBatch = (values, setSubmitting, closeModalHandle) => {
    let valuesDto = {
      description: values.description,
      dateScheduled: values.dateScheduled,
      status: values.status,
      projectedSavings: values.projectedSavings,
      numberOfSwitch: values.numberOfSwitch,
      wfBatchId: values.wfBatchId,
    };

    sendAuthenticatedAsyncRequest(
      `/swim/update-batch`,
      "PUT",
      valuesDto,
      (r) => {
        setSubmitting(false);
        showNotification({
          title: "Action successful!",
          message: "Batch edited successfully.",
          position: "tc",
          type: "success",
        });
        closeModalHandle();
        handler();
      },
      (r) => {
        setSubmitting(false);
        showNotification({
          title: "Error!",
          message:
            getErrorMessage(r.data) || getErrorMessage("UNABLE_TO_EDIT_BATCH"),
          position: "tc",
          type: "error",
        });
      }
    );
  };

  const invertShowbutton = () => {
    setShowButton((prev) => !prev);
  };

  const editBatchFormSchema = Yup.object().shape({
    description: Yup.string().max(100, "Maximum Length: " + 100),
    dateScheduled: Yup.string().required("Date Scheduled is required"),
    status: Yup.string().required("Status is required"),
    projectedSavings: Yup.string().min(0.0, "Minimum Savings: " + 0.0),
    totalSwitches: Yup.string().min(0, "Minimum Switches: " + 0),
  });

  if (showButton) {
    return (
      <OverlayTrigger overlay={<Tooltip>Edit Batch</Tooltip>}>
        <MdModeEdit
          size={20}
          className="user-action-button edit-user"
          onClick={invertShowbutton}
        />
      </OverlayTrigger>
    );
  } else {
    return (
      <>
        <OverlayTrigger overlay={<Tooltip>Edit Batch</Tooltip>}>
          <MdModeEdit
            className="user-action-button edit-user"
            size={20}
            onClick={invertShowbutton}
          />
        </OverlayTrigger>
        <AdminPopUpDismissableModal
          ref={modalRef}
          openModalButtonText="Edit Batch"
          openModalButtonStyle="info"
          openModalButtonBlock={true}
          openModalButtonDisabled={false}
          openModalBsuttonId="edit-batch-btn-popup"
          preventCloseWhenClickedOutsideModal={true}
          initialStateOpen={true}
          hideFooter={true}
          onClose={invertShowbutton}
          title={"Edit Batch Details"}
          showIcons={true}
        >
          <Formik
            initialValues={{ ...batch }}
            onSubmit={(values, { setSubmitting }) => {
              editBatch(values, setSubmitting, modalRef.current.handleClose);
            }}
            validationSchema={editBatchFormSchema}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.description}
                        onChange={handleChange}
                        name="description"
                        onBlur={handleBlur}
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.description &&
                          touched.description &&
                          errors.description}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Batch Status</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.status}
                        onChange={handleChange}
                        name="status"
                        onBlur={handleBlur}
                        readOnly
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.status && touched.status && errors.status}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Date Scheduled</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatDateWithTime(values.dateScheduled)}
                        onChange={handleChange}
                        name="dateScheduled"
                        onBlur={handleBlur}
                        readOnly
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.dateScheduled &&
                          touched.dateScheduled &&
                          errors.dateScheduled}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Total Switches</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.numberOfSwitch}
                        onChange={handleChange}
                        name="numberOfSwitch"
                        onBlur={handleBlur}
                        readOnly
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.numberOfSwitch &&
                          touched.numberOfSwitch &&
                          errors.numberOfSwitch}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Projected Savings</Form.Label>
                      <Form.Control
                        type="text"
                        value={twoDecimalPlaceRounder(values.projectedSavings)}
                        onChange={handleChange}
                        name="projectedSavings"
                        onBlur={handleBlur}
                        readOnly
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.projectedSavings &&
                          touched.projectedSavings &&
                          errors.projectedSavings}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="bottom-spacer top-spacer">
                  <Col md={{ offset: "4", span: "4" }}>
                    <Button
                      variant="outline-dark"
                      block
                      onClick={() => modalRef.current.handleClose()}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col md={4}>
                    <Button
                      type="submit"
                      variant="success"
                      block
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Editing... " : "Edit"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </AdminPopUpDismissableModal>
      </>
    );
  }
};

export default EditBatch;